// AGC: Voice Agent v2 — typed client for /api/v1/voice/agent-v2. No TTS, no polling.

import { getBackendUrl } from '../utils/backendUrl'
import { useAuthStore } from './authStore'

export interface ToolCallCard {
  name: string
  input?: Record<string, unknown>
  result?: { success?: boolean; [key: string]: unknown }
  error?: string
}

export interface PendingConfirmation {
  tool: string
  data?: Record<string, unknown>
  prompt?: string
}

export interface VoiceAgentV2Response {
  success: boolean
  transcription: string
  replyText: string
  toolCallCards: ToolCallCard[]
  entryCreated: boolean
  entryType: string | null
  pendingConfirmation: PendingConfirmation | null
  sessionId: string
  empty?: boolean
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await useAuthStore.getState().getAuthToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export async function sendToAgentV2(
  audioBlob: Blob,
  lang: string,
  opts: { reset?: boolean } = {}
): Promise<VoiceAgentV2Response> {
  const backendUrl = getBackendUrl()
  const formData = new FormData()
  formData.append('audio', audioBlob, 'recording.webm')
  formData.append('lang', lang)
  if (opts.reset) formData.append('reset', '1')
  const res = await fetch(`${backendUrl}/api/v1/voice/agent-v2`, {
    method: 'POST',
    headers: await authHeaders(),
    body: formData,
  })
  if (!res.ok) throw new Error(`agent-v2 ${res.status}`)
  return res.json()
}

export async function sendTextToAgentV2(
  text: string,
  lang: string
): Promise<VoiceAgentV2Response> {
  const backendUrl = getBackendUrl()
  const res = await fetch(`${backendUrl}/api/v1/voice/agent-v2/text`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ text, lang }),
  })
  if (!res.ok) throw new Error(`agent-v2/text ${res.status}`)
  return res.json()
}

export async function confirmAgentAction(
  action: 'confirm' | 'reject',
  lang: string
): Promise<VoiceAgentV2Response> {
  const backendUrl = getBackendUrl()
  const res = await fetch(`${backendUrl}/api/v1/voice/agent-v2/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ action, lang }),
  })
  if (!res.ok) throw new Error(`agent-v2/confirm ${res.status}`)
  return res.json()
}
